import { useEffect } from "react";
import { userProfilesApi } from "@/api/services";
import { browserTimeZone } from "@/lib/browserTimeZone";
import { useAuthStore } from "@/stores/auth";

const SENT_KEY = "ftl_tz_recorded";

/**
 * Send this browser's time zone once per sign-in and org, so the Team page can
 * show each member in their own zone (row 511fdc25; founder #3615 6022010430
 * item 9: "Everyone but me is on UTC"). The server stores it only while the
 * member never chose a zone in Settings, so a choice is never overwritten.
 * Fire-and-forget: a failure is retried on the next sign-in and never shown.
 */
export function useRecordBrowserTimeZone(): void {
  const userId = useAuthStore((s) => s.user?.id);
  const orgId = useAuthStore((s) => s.user?.org_id);

  useEffect(() => {
    if (!userId || !orgId) return;
    const tz = browserTimeZone();
    if (!tz || tz === "UTC" || tz === "Etc/UTC") return;
    const key = `${userId}:${orgId}:${tz}`;
    try {
      if (sessionStorage.getItem(SENT_KEY) === key) return;
    } catch {
      // Storage unavailable: send anyway; the server write is idempotent.
    }
    userProfilesApi
      .recordDetectedTimeZone(tz)
      .then(() => {
        try {
          sessionStorage.setItem(SENT_KEY, key);
        } catch {
          // Not remembered: the next load sends it again, harmlessly.
        }
      })
      .catch(() => {
        // Never surfaced: the Team page reads "Time zone not set" until it lands.
      });
  }, [userId, orgId]);
}
